import { useState } from "react";
import { ApiError } from "@/api/client";
import {
  CONFIG_EXPORT_URL,
  trainingExportUrl,
  useConnections,
  useFileStorage,
  useLimits,
  useNotices,
  useNotificationSettings,
  useResetNotificationSettings,
  useRetention,
  useRunRetention,
  useEvalSuites,
  useImportConfig,
  usePromoteToEvalCase,
  usePromoteToNewEvalSuite,
  useTrainingLog,
  useUpdateLimits,
  useUpdateNotificationSettings,
  type NotificationSettings,
  type RetentionPolicy,
  type TrainingFilters,
} from "@/api/queries";
import { Button } from "@/components/ui/Button";
import { Card, CardBody, CardHeader } from "@/components/ui/Card";
import { formatBytes } from "@/lib/bytes";
import { Chip, StatusDot } from "@/components/ui/Chip";
import { ConfirmDialog } from "@/components/ui/ConfirmDialog";
import { EmptyState } from "@/components/ui/EmptyState";
import { Field } from "@/components/ui/Field";
import { Menu } from "@/components/ui/Menu";
import { Select, Toggle } from "@/components/ui/Select";
import { Spinner } from "@/components/ui/Spinner";
import {
  AlertIcon,
  ChatIcon,
  ClockIcon,
  DownloadIcon,
  GaugeIcon,
  MailIcon,
  TrashIcon,
} from "@/components/icons";
import { cn } from "@/lib/cn";
import { DemoData, DeploymentFacts } from "@/components/operations/DeploymentFacts";

/**
 * Operations: limits, retention and the training log (FR-10.4, FR-10.6, FR-4.10).
 *
 * The three admin surfaces that are about the deployment rather than the data.
 * Two of them are honesty features: a limit you cannot see is one you cannot
 * reason about, and a retention job that deletes silently is indistinguishable
 * from a bug — so the page shows what each policy *would* remove before it
 * removes it, and what the last sweep actually took.
 *
 * The training log is deliberately labelled as what it is: a view over the
 * conversations, not a separate copy of them. Which is why pruning it deletes
 * threads, and why the page says so where the policy is.
 */

// --------------------------------------------------------------------------
// Limits (PRD FR-10.4)
// --------------------------------------------------------------------------

/**
 * Operator notices (ALERT-1): who is told when a schedule starts failing,
 * and the record of every time it happened — emailed or not. The list is the
 * half that still works when SMTP is what broke.
 */
function OperatorNotices() {
  const { data } = useNotificationSettings();
  const { data: notices } = useNotices();
  return (
    <Card>
      <CardHeader
        title="Operator notices"
        description="One email when a schedule starts failing, one when it recovers — never one per failed run"
        action={<MailIcon className="text-ink-disabled size-5" />}
      />
      <CardBody className="flex flex-col gap-5">
        {data ? <NoticeSettingsForm key={data.emails.join(",") + data.source} state={data} /> : <Spinner />}
        <div>
          <p className="type-label text-ink mb-2">Recent notices</p>
          {notices && notices.length ? (
            <ul className="divide-border border-border rounded-element divide-y border">
              {notices.map((notice) => (
                <li key={notice.id} className="flex flex-wrap items-center gap-2 px-3 py-2">
                  <StatusDot tone={notice.kind === "schedule.failing" ? "error" : "success"} />
                  <span className="type-support text-ink min-w-40 flex-1">
                    {notice.kind === "schedule.failing" ? "Started failing" : "Recovered"}:{" "}
                    {notice.schedule_name}
                    <span className="text-ink-secondary block">{notice.reason}</span>
                  </span>
                  <Chip
                    tone={
                      notice.delivery_status === "sent"
                        ? "success"
                        : notice.delivery_status === "failed"
                          ? "error"
                          : "neutral"
                    }
                  >
                    {notice.delivery_status === "sent" ? "emailed" : notice.delivery_status}
                  </Chip>
                  <span className="type-caption text-ink-disabled normal-case">
                    {new Date(notice.created_at).toLocaleString()}
                  </span>
                </li>
              ))}
            </ul>
          ) : (
            <p className="type-support text-ink-secondary">
              Nothing has broken yet. Each transition appears here, whether or not it could be
              emailed.
            </p>
          )}
        </div>
      </CardBody>
    </Card>
  );
}

function NoticeSettingsForm({ state }: { state: NotificationSettings }) {
  const update = useUpdateNotificationSettings();
  const reset = useResetNotificationSettings();
  const [emails, setEmails] = useState(state.emails.join(", "));
  const [recovery, setRecovery] = useState(state.notify_recovery);
  const parsed = emails
    .split(/[,;\s]+/)
    .map((value) => value.trim())
    .filter(Boolean);
  const dirty =
    parsed.join(",") !== state.emails.join(",") || recovery !== state.notify_recovery;
  return (
    <div className="flex flex-col gap-3">
      <Field
        label="Operator email"
        placeholder="ops@example.com, oncall@example.com"
        value={emails}
        onChange={(event) => setEmails(event.target.value)}
        error={update.error instanceof ApiError ? update.error.message : undefined}
        hint={
          state.source === "environment"
            ? "Seeded from MING_OPERATOR_EMAIL. Saving here overrides it."
            : state.source === "settings"
              ? "Set here, overriding MING_OPERATOR_EMAIL."
              : "Not set — notices are recorded below but not emailed."
        }
      />
      <Toggle
        label="Also email when a schedule recovers"
        checked={recovery}
        onChange={setRecovery}
      />
      {!state.email_configured ? (
        <p className="bg-warning-subtle text-warning type-support rounded-element flex items-start gap-2 px-3 py-2">
          <AlertIcon className="mt-0.5 size-3.5 shrink-0" />
          No SMTP server is configured, so notices are only shown here.
        </p>
      ) : null}
      <div className="flex items-center gap-2">
        <Button
          variant="primary"
          disabled={!dirty}
          loading={update.isPending}
          onClick={() => update.mutate({ emails: parsed, notify_recovery: recovery })}
        >
          Save
        </Button>
        {state.source === "settings" ? (
          <Button variant="ghost" loading={reset.isPending} onClick={() => reset.mutate()}>
            Use the environment&rsquo;s address
          </Button>
        ) : null}
      </div>
    </div>
  );
}

function Limits() {
  const { data, isLoading } = useLimits();
  const update = useUpdateLimits();
  const [runs, setRuns] = useState<number | null>(null);
  const [chat, setChat] = useState<number | null>(null);

  if (isLoading || !data) return <Spinner />;

  const runsValue = runs ?? data.max_concurrent_runs;
  const chatValue = chat ?? data.chat_rate_limit_messages;
  const dirty = runsValue !== data.max_concurrent_runs || chatValue !== data.chat_rate_limit_messages;
  const error = update.error instanceof ApiError ? update.error.message : null;
  const saturated = data.active_runs >= data.max_concurrent_runs;

  return (
    <Card>
      <CardHeader
        title="Limits"
        description="What this deployment refuses, and how close it is to refusing"
      />
      <CardBody className="flex flex-col gap-5">
        <div className="grid gap-3 sm:grid-cols-3">
          <div className="border-border bg-card rounded-container border p-3">
            <span className="type-caption text-ink-disabled inline-flex items-center gap-1.5">
              <GaugeIcon className="size-3.5" />
              Agent runs in flight
            </span>
            <p className={cn("type-metric mt-1", saturated ? "text-warning" : "text-ink")}>
              {data.active_runs} / {data.max_concurrent_runs}
            </p>
            {saturated ? (
              <p className="type-support text-warning mt-1">
                At capacity — the next question is told to try again in a moment.
              </p>
            ) : null}
          </div>
          <div className="border-border bg-card rounded-container border p-3">
            <span className="type-caption text-ink-disabled">Chat clients throttled</span>
            <p
              className={cn(
                "type-metric mt-1",
                data.chat_clients_blocked > 0 ? "text-warning" : "text-ink",
              )}
            >
              {data.chat_clients_blocked}
            </p>
            <p className="type-support text-ink-secondary mt-1">
              {data.chat_clients_tracked} active in the window
            </p>
          </div>
          <div className="border-border bg-card rounded-container border p-3">
            <span className="type-caption text-ink-disabled">Unlock clients blocked</span>
            <p
              className={cn(
                "type-metric mt-1",
                data.unlock_clients_blocked > 0 ? "text-warning" : "text-ink",
              )}
            >
              {data.unlock_clients_blocked}
            </p>
            <p className="type-support text-ink-secondary mt-1">
              {data.unlock_rate_limit_attempts} attempts per{" "}
              {Math.round(data.unlock_rate_limit_window_seconds / 60)} min
            </p>
          </div>
        </div>

        <div className="flex flex-wrap items-end gap-4">
          <label className="flex flex-col gap-2">
            <span className="type-label text-ink">Concurrent agent runs</span>
            <input
              type="number"
              min={1}
              max={64}
              value={runsValue}
              onChange={(event) => setRuns(Number(event.target.value))}
              className="border-border bg-card text-ink control-lg rounded-element w-28 border px-3 type-body"
            />
          </label>
          <label className="flex flex-col gap-2">
            <span className="type-label text-ink">
              Chat messages per {data.chat_rate_limit_window_seconds}s
            </span>
            <input
              type="number"
              min={1}
              max={10000}
              value={chatValue}
              onChange={(event) => setChat(Number(event.target.value))}
              className="border-border bg-card text-ink control-lg rounded-element w-28 border px-3 type-body"
            />
          </label>
          <Button
            variant="primary"
            type="button"
            loading={update.isPending}
            disabled={!dirty}
            onClick={() =>
              update.mutate(
                { max_concurrent_runs: runsValue, chat_rate_limit_messages: chatValue },
                { onSuccess: () => { setRuns(null); setChat(null); } },
              )
            }
          >
            Apply
          </Button>
          {error ? <span className="type-support text-error">{error}</span> : null}
        </div>

        <p className="type-support text-ink-secondary">
          A change applies immediately and survives a restart. The environment
          (<span className="font-mono">MING_AGENT_MAX_CONCURRENT_RUNS</span>,{" "}
          <span className="font-mono">MING_CHAT_RATE_LIMIT_MESSAGES</span>) sets the value this
          deployment boots with.
          {data.overridden.length > 0 ? (
            <> Currently overridden here: {data.overridden.join(", ")}.</>
          ) : null}{" "}
          A run is also bounded by its tool budget ({data.tool_budget_soft} soft,{" "}
          {data.tool_budget_hard} hard) and a {data.agent_run_timeout_seconds}s hard cap.
        </p>
      </CardBody>
    </Card>
  );
}

// --------------------------------------------------------------------------
// Retention (PRD FR-10.6)
// --------------------------------------------------------------------------

function PolicyRow({ policy }: { policy: RetentionPolicy }) {
  const off = policy.days <= 0;
  return (
    <li className="border-border border-b py-3 last:border-b-0">
      <div className="flex flex-wrap items-center gap-2">
        <span className="type-label text-ink">{policy.label}</span>
        {off ? (
          <Chip tone="neutral">kept forever</Chip>
        ) : (
          <Chip tone="accent">{policy.days} days</Chip>
        )}
        {policy.destructive ? <Chip tone="warning">destructive</Chip> : null}
        <span className="type-support text-ink-secondary tabular-nums ms-auto">
          {policy.rows.toLocaleString()} rows
          {policy.expiring > 0 ? (
            <span className="text-warning"> · {policy.expiring.toLocaleString()} expiring</span>
          ) : null}
        </span>
      </div>
      <p className="type-support text-ink-secondary mt-1">{policy.description}</p>
      <p className="type-caption text-ink-disabled mt-1 font-mono">{policy.setting}</p>
    </li>
  );
}

function Retention() {
  const { data, isLoading } = useRetention();
  const run = useRunRetention();
  const [confirm, setConfirm] = useState(false);

  if (isLoading || !data) return <Spinner />;
  const expiring = data.policies.reduce((total, policy) => total + policy.expiring, 0);

  return (
    <Card>
      <CardHeader
        title="Retention"
        description="What this deployment keeps, and for how long"
        action={
          <div className="flex items-center gap-2">
            <StatusDot tone={data.enabled ? "success" : "warning"} />
            <span className="type-support text-ink-secondary">
              {data.enabled
                ? `Sweeps daily at ${String(data.at_hour).padStart(2, "0")}:00 ${data.timezone}`
                : "Sweeps are off"}
            </span>
          </div>
        }
      />
      <CardBody className="flex flex-col gap-4">
        {!data.enabled ? (
          <p className="type-support text-warning inline-flex items-start gap-2">
            <AlertIcon className="mt-0.5 size-4 shrink-0" />
            <span>
              <span className="font-mono">MING_RETENTION_ENABLED=false</span>, so every policy
              below is inert. Nothing is being deleted, whatever the numbers say.
            </span>
          </p>
        ) : null}

        <ul className="border-border bg-card rounded-container border px-4">
          {data.policies.map((policy) => (
            <PolicyRow key={policy.key} policy={policy} />
          ))}
        </ul>

        <div className="flex flex-wrap items-center gap-3">
          {confirm ? (
            <>
              <Button
                variant="primary"
                type="button"
                icon={<TrashIcon className="size-4" />}
                loading={run.isPending}
                onClick={() => run.mutate(undefined, { onSuccess: () => setConfirm(false) })}
              >
                Yes, delete {expiring.toLocaleString()} rows now
              </Button>
              <Button type="button" variant="ghost" onClick={() => setConfirm(false)}>
                Cancel
              </Button>
            </>
          ) : (
            <Button type="button" onClick={() => setConfirm(true)}>
              Run retention now
            </Button>
          )}
          <span className="type-support text-ink-secondary">
            {expiring > 0
              ? `${expiring.toLocaleString()} rows are past their policy.`
              : "Nothing is past its policy."}
          </span>
        </div>

        {data.runs.length > 0 ? (
          <div>
            <h3 className="type-h4 text-ink mb-2 inline-flex items-center gap-2">
              <ClockIcon className="size-4" />
              Recent sweeps
            </h3>
            <ul className="flex flex-col gap-1">
              {data.runs.slice(0, 8).map((entry) => (
                <li
                  key={entry.id}
                  className="border-border rounded-element flex flex-wrap items-center gap-2 border px-3 py-2"
                >
                  <Chip tone={entry.status === "ok" ? "success" : "error"}>{entry.status}</Chip>
                  <Chip tone="neutral">{entry.trigger}</Chip>
                  <span className="type-support text-ink-secondary tabular-nums">
                    {entry.total_deleted.toLocaleString()} rows
                  </span>
                  {Object.entries(entry.deleted).length > 0 ? (
                    <span className="type-support text-ink-disabled">
                      {Object.entries(entry.deleted)
                        .map(([key, count]) => `${key} ${count}`)
                        .join(" · ")}
                    </span>
                  ) : null}
                  <span className="type-caption text-ink-disabled ms-auto">
                    {new Date(entry.started_at).toLocaleString()}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        ) : null}
      </CardBody>
    </Card>
  );
}

// --------------------------------------------------------------------------
// Training log (PRD FR-4.10)
// --------------------------------------------------------------------------

/**
 * "Promote this answer to a case" (EVAL-1): its question, and the shape-facts
 * it actually had, become an eval case the next run grades. Only suites on the
 * sources the answer read are offered (MSE-3) — anything else would ask its
 * question of databases it was never answered from — plus a new suite on them.
 */
function PromoteToSuite({ messageId, connectionIds }: { messageId: string; connectionIds: string[] }) {
  const { data: suites } = useEvalSuites();
  const promote = usePromoteToEvalCase();
  const create = usePromoteToNewEvalSuite();
  const [done, setDone] = useState<string | null>(null);
  if (!suites) return null;
  if (done) return <Chip tone="success">added to {done}</Chip>;
  const wanted = [...connectionIds].sort().join(" ");
  const matching = suites.filter(
    (suite) =>
      suite.missing_sources.length === 0 && [...suite.connection_ids].sort().join(" ") === wanted,
  );
  const item = "type-support text-ink hover:bg-hover rounded-inner px-2 py-1.5 text-start";
  return (
    <Menu
      label="Add to eval suite"
      trigger={(props) => (
        <Button size="sm" variant="ghost" loading={promote.isPending || create.isPending} {...props}>
          Add to eval suite
        </Button>
      )}
    >
      {(close) => (
        <>
          {matching.map((suite) => (
            <button
              key={suite.id}
              type="button"
              role="menuitem"
              className={item}
              onClick={() => {
                close();
                promote.mutate(
                  { suiteId: suite.id, messageId },
                  { onSuccess: () => setDone(suite.name) },
                );
              }}
            >
              {suite.name}
            </button>
          ))}
          <button
            type="button"
            role="menuitem"
            className={cn(item, matching.length ? "border-border border-t" : "")}
            onClick={() => {
              close();
              create.mutate(messageId, { onSuccess: (made) => setDone(made.name) });
            }}
          >
            New suite on {connectionIds.length > 1 ? "these sources" : "this source"}
          </button>
        </>
      )}
    </Menu>
  );
}

function TrainingLog() {
  const [filters, setFilters] = useState<TrainingFilters>({ limit: 10 });
  const { data: connections } = useConnections();
  const { data, isLoading } = useTrainingLog(filters);

  return (
    <Card>
      <CardHeader
        title="Training log"
        description="Every answer, with the question, the SQL that produced it, and the reader's verdict"
        action={
          <a
            href={trainingExportUrl(filters)}
            className="type-support text-accent inline-flex items-center gap-1.5 hover:underline"
          >
            <DownloadIcon className="size-4" />
            Export JSONL
          </a>
        }
      />
      <CardBody className="flex flex-col gap-4">
        <p className="type-support text-ink-secondary">
          A view over the conversations rather than a separate copy of them, so every example
          carries the statement that actually ran. Regenerated answers appear twice on purpose —
          comparing two attempts to the same question is what makes this useful for evals.
        </p>

        <div className="flex flex-wrap items-end gap-3">
          <div className="w-56">
            <Select
              label="Source"
              value={filters.connection_id ?? ""}
              onChange={(event) =>
                setFilters((current) => ({
                  ...current,
                  connection_id: event.target.value || undefined,
                }))
              }
            >
              <option value="">Every source</option>
              {(connections ?? []).map((connection) => (
                <option key={connection.id} value={connection.id}>
                  {connection.name}
                </option>
              ))}
            </Select>
          </div>
          <label className="flex items-center gap-2 pb-2">
            <input
              type="checkbox"
              checked={Boolean(filters.rated_only)}
              onChange={(event) =>
                setFilters((current) => ({ ...current, rated_only: event.target.checked }))
              }
              className="accent-accent size-4"
            />
            <span className="type-label text-ink">Only rated answers</span>
          </label>
        </div>

        {isLoading || !data ? (
          <Spinner />
        ) : data.total === 0 ? (
          <EmptyState
            icon={<ChatIcon className="size-6" />}
            title="No answers yet"
            detail="Every question this deployment answers lands here, with its SQL and its rating."
          />
        ) : (
          <>
            <div className="flex flex-wrap items-center gap-3">
              <Chip tone="accent">{data.summary.examples.toLocaleString()} examples</Chip>
              <Chip tone="neutral">{data.summary.rated.toLocaleString()} rated</Chip>
              <Chip tone={data.summary.net_feedback >= 0 ? "success" : "error"}>
                net {data.summary.net_feedback >= 0 ? "+" : ""}
                {data.summary.net_feedback}
              </Chip>
            </div>

            <ul className="flex flex-col gap-2">
              {data.examples.map((example) => (
                <li
                  key={example.id}
                  className="border-border bg-card rounded-container border p-3"
                >
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="type-label text-ink truncate">
                      {example.question || "—"}
                    </span>
                    {example.feedback === 1 ? <Chip tone="success">👍</Chip> : null}
                    {example.feedback === -1 ? <Chip tone="error">👎</Chip> : null}
                    {example.thread_kind !== "chat" ? (
                      <Chip tone="accent">{example.thread_kind}</Chip>
                    ) : null}
                    {example.error_code ? (
                      <Chip tone="warning">{example.error_code}</Chip>
                    ) : null}
                    <span className="type-caption text-ink-disabled ms-auto">
                      {new Date(example.occurred_at).toLocaleString()}
                    </span>
                  </div>
                  <p className="type-support text-ink-secondary mt-1 line-clamp-2">
                    {example.answer}
                  </p>
                  <div className="mt-1 flex flex-wrap items-center gap-2">
                    <p className="type-caption text-ink-disabled flex-1">
                      {example.connection_name || "no source"} · {example.model_label ?? "—"} ·{" "}
                      {example.statements.length} statement
                      {example.statements.length === 1 ? "" : "s"}
                    </p>
                    {example.question && !example.error_code ? (
                      <PromoteToSuite
                        messageId={example.id}
                        connectionIds={
                          example.connection_ids?.length
                            ? example.connection_ids
                            : example.connection_id
                              ? [example.connection_id]
                              : []
                        }
                      />
                    ) : null}
                  </div>
                </li>
              ))}
            </ul>

            <p className="type-support text-ink-secondary">
              Showing {data.examples.length} of {data.total.toLocaleString()}. The export carries
              every matching example, oldest first.
            </p>
          </>
        )}
      </CardBody>
    </Card>
  );
}

const ACTION_TONE: Record<string, string> = {
  create: "text-success",
  update: "text-accent",
  unchanged: "text-ink-disabled",
  skip: "text-warning",
  failed: "text-error",
};

/**
 * Configuration export and import (M8.4): sources, providers, catalog
 * decisions, prompt, brand, limits, operator notices and eval suites — never
 * a secret and never data. An import is always planned first; applying it is
 * a second, confirmed step.
 */
function ConfigTransfer() {
  const importer = useImportConfig();
  const [document, setDocument] = useState<Record<string, unknown> | null>(null);
  const [fileName, setFileName] = useState("");
  const [confirming, setConfirming] = useState(false);
  const result = importer.data;

  async function choose(file: File | undefined) {
    if (!file) return;
    setFileName(file.name);
    let parsed: Record<string, unknown>;
    try {
      parsed = JSON.parse(await file.text()) as Record<string, unknown>;
    } catch {
      setDocument(null);
      importer.reset();
      return;
    }
    setDocument(parsed);
    importer.mutate({ document: parsed, dry_run: true });
  }

  return (
    <Card>
      <CardHeader
        title="Configuration"
        description="Move this deployment's set-up to another one: sources and their dictionaries, providers, catalog decisions, the prompt, the brand, limits, operator notices and eval suites. Passwords, API keys and data never travel — a backup carries data."
      />
      <CardBody className="flex flex-col gap-4">
        <div className="flex flex-wrap items-center gap-3">
          <a
            href={CONFIG_EXPORT_URL}
            // No name here: the server's is the deployment's own
            // (`paige-config.json`), and a `download` value would override it.
            download
            className="type-support text-accent hover:underline"
          >
            Export configuration
          </a>
          <label className="type-support text-accent cursor-pointer hover:underline">
            Import a configuration…
            <input
              type="file"
              accept="application/json,.json"
              className="sr-only"
              onChange={(event) => void choose(event.target.files?.[0])}
            />
          </label>
          {fileName ? <span className="type-caption text-ink-disabled normal-case">{fileName}</span> : null}
        </div>
        {fileName && !document ? (
          <p className="type-support text-error">That file is not JSON, so it is not a configuration export.</p>
        ) : null}
        {importer.isPending ? <Spinner /> : null}
        {result ? (
          <div className="flex flex-col gap-3">
            <p className="type-support text-ink-secondary">
              {result.dry_run
                ? `The plan: ${result.changed} change${result.changed === 1 ? "" : "s"}. Nothing has been changed yet.`
                : `Applied: ${result.changed} change${result.changed === 1 ? "" : "s"}, each recorded in the audit log.`}
            </p>
            <ul className="border-border divide-border divide-y rounded-element border">
              {result.items.map((item) => (
                <li key={`${item.kind}:${item.name}`} className="flex flex-col gap-1 px-3 py-2">
                  <span className="type-support flex flex-wrap gap-2">
                    <span className={cn("font-medium", ACTION_TONE[item.action])}>{item.action}</span>
                    <span className="text-ink-disabled">{item.kind}</span>
                    <span className="text-ink">{item.name}</span>
                  </span>
                  {item.notes.map((note) => (
                    <span key={note} className="type-caption text-ink-secondary normal-case">
                      {note}
                    </span>
                  ))}
                </li>
              ))}
            </ul>
            {result.dry_run && result.changed > 0 && document ? (
              <div>
                <Button variant="primary" onClick={() => setConfirming(true)}>
                  Apply {result.changed} change{result.changed === 1 ? "" : "s"}
                </Button>
              </div>
            ) : null}
          </div>
        ) : null}
      </CardBody>
      <ConfirmDialog
        open={confirming}
        onClose={() => setConfirming(false)}
        onConfirm={() => {
          if (!document) return;
          importer.mutate(
            { document, dry_run: false },
            { onSettled: () => setConfirming(false) },
          );
        }}
        title="Apply this configuration?"
        description={`${result?.changed ?? 0} change${result?.changed === 1 ? "" : "s"} from ${fileName}.`}
        consequence="Sources, providers and the prompt change for everyone, now. Each change is audited and can be edited back by hand; it is not one undoable step."
        confirmLabel="Apply"
        loading={importer.isPending}
      />
    </Card>
  );
}

/**
 * The files volume (FILE-1): what uploaded sources occupy, what is left, and
 * the largest upload that would be accepted right now. Read from disk on
 * every request — nothing here is a stored number that could drift.
 */
function Storage() {
  const { data, isLoading } = useFileStorage();
  if (isLoading || !data) return <Spinner />;

  const volumeUsed = data.volume_total - data.volume_free;
  const share = data.volume_total ? volumeUsed / data.volume_total : 0;
  const tight = data.available < Math.min(data.max_file, 1_000_000_000);
  const largest = data.sources.slice(0, 8);

  return (
    <Card>
      <CardHeader
        title="Storage"
        description="Uploaded sources, on their own volume where the deployment has one"
      />
      <CardBody className="flex flex-col gap-5">
        <div className="grid gap-3 sm:grid-cols-3">
          <div className="border-border bg-card rounded-container border p-3">
            <span className="type-caption text-ink-disabled">Uploaded sources</span>
            <p className="type-metric text-ink mt-1 tabular-nums">{formatBytes(data.used)}</p>
            <p className="type-support text-ink-secondary mt-1">
              {data.quota ? `of a ${formatBytes(data.quota)} quota` : "no quota set"}
            </p>
          </div>
          <div className="border-border bg-card rounded-container border p-3">
            <span className="type-caption text-ink-disabled">Volume</span>
            <p className="type-metric text-ink mt-1 tabular-nums">
              {formatBytes(data.volume_free)} free
            </p>
            <div
              role="meter"
              aria-label="Volume used"
              aria-valuemin={0}
              aria-valuemax={100}
              aria-valuenow={Math.round(share * 100)}
              className="bg-muted mt-2 h-1.5 overflow-hidden rounded-full"
            >
              <div
                className={cn("h-full rounded-full", share > 0.85 ? "bg-warning" : "bg-accent")}
                style={{ width: `${Math.round(share * 100)}%` }}
              />
            </div>
            <p className="type-support text-ink-secondary mt-1">
              {formatBytes(volumeUsed)} used of {formatBytes(data.volume_total)}
            </p>
          </div>
          <div className="border-border bg-card rounded-container border p-3">
            <span className="type-caption text-ink-disabled">Largest upload accepted now</span>
            <p className={cn("type-metric mt-1 tabular-nums", tight ? "text-warning" : "text-ink")}>
              {formatBytes(Math.min(data.available, data.max_file))}
            </p>
            <p className="type-support text-ink-secondary mt-1">
              {formatBytes(data.max_file)} per file · {formatBytes(data.max_xlsx)} per workbook ·{" "}
              {formatBytes(data.reserve)} kept free
            </p>
          </div>
        </div>
        {largest.length ? (
          <table className="type-support w-full">
            <thead>
              <tr className="text-ink-disabled text-left">
                <th className="type-caption pb-2 font-normal">Source</th>
                <th className="type-caption pb-2 font-normal">Kind</th>
                <th className="type-caption pb-2 text-right font-normal">Size</th>
              </tr>
            </thead>
            <tbody>
              {largest.map((source) => (
                <tr key={source.connection_id} className="border-border border-t">
                  <td className="text-ink py-2">
                    {source.name ?? (
                      <span className="text-warning">
                        No source owns this directory ({source.connection_id})
                      </span>
                    )}
                  </td>
                  <td className="text-ink-secondary py-2">{source.kind_label ?? "—"}</td>
                  <td className="text-ink py-2 text-right tabular-nums">
                    {formatBytes(source.bytes)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <p className="type-support text-ink-secondary">No uploaded sources yet.</p>
        )}
        <p className="type-caption text-ink-disabled normal-case">{data.directory}</p>
      </CardBody>
    </Card>
  );
}

export function OperationsPage() {
  return (
    <div className="mx-auto w-full max-w-5xl px-4 py-8 sm:px-8 sm:py-10">
      <header className="mb-8">
        <p className="type-caption text-ink-disabled">Admin</p>
        <h1 className="type-h1 text-ink mt-2">Operations</h1>
        <p className="type-body text-ink-secondary mt-3 max-w-2xl">
          What this deployment is running, the limits it enforces, the data it keeps, and the
          record of what it has been asked.
        </p>
      </header>

      <div className="flex flex-col gap-5">
        <DeploymentFacts />
        <OperatorNotices />
        <Limits />
        <Storage />
        <DemoData />
        <Retention />
        <TrainingLog />
        <ConfigTransfer />
      </div>
    </div>
  );
}
